import { useBaultDatas } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { useKodiakFarmByPool, useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import { useMemo } from 'react'

import { StakingData } from '../../config'

interface UseStakingPositionParams {
  stakingData: StakingData
}

export function useStakingPosition({ stakingData }: UseStakingPositionParams) {
  const { account } = useConnection()

  // Get farm object from farmAddress
  const kodiakFarm = useKodiakFarmByPool(stakingData.kodiakFarmAddress)

  // Farm data - only fetch when stakingType is 'farm'
  const { data: userStakesData, isLoading: isLoadingUserStakes } = useKodiakFarmLockedStakesOf(
    stakingData.stakingType === 'kodiakFarm' ? kodiakFarm : null,
    account
  )

  // Vault data - only fetch when stakingType is 'vault'
  const { data: rewardVaultData, isLoading: isLoadingVault } = useRewardVaultDatas(
    stakingData.stakingType === 'rewardVault' ? stakingData.rewardVaultAddress : undefined,
    account
  )

  // Bault data - only for vaults with bault address
  const { data: baultData, isLoading: isLoadingBault } = useBaultDatas(
    stakingData.stakingType === 'rewardVault' && stakingData.baultAddress ? stakingData.baultAddress : undefined,
    account
  )

  // Unified staking position data
  const position = useMemo(() => {
    const isLoading = isLoadingUserStakes || isLoadingVault || isLoadingBault

    switch (stakingData.stakingType) {
      case 'kodiakFarm':
        return {
          type: 'kodiakFarm' as const,
          userStaked: userStakesData?.totalLiquidity,
          userStakedUSD: userStakesData?.totalLiquidityUSD,
          stakes: userStakesData?.stakes || [],
          isLoading,
        }

      case 'rewardVault': {
        // Priority: direct vault staking > bault staking
        const directVaultStaked = rewardVaultData?.selfStaked && rewardVaultData.selfStaked.greaterThan(0)
        const baultStaked = baultData?.userShares && baultData.userShares.greaterThan(0)

        if (directVaultStaked) {
          return {
            type: 'rewardVault' as const,
            subType: 'direct' as const,
            userStaked: rewardVaultData.selfStaked,
            userStakedUSD: rewardVaultData.userStakedUSD,
            stakes: rewardVaultData.positions || [],
            isLoading,
          }
        } else if (baultStaked) {
          return {
            type: 'rewardVault' as const,
            subType: 'bault' as const,
            userStaked: baultData.userShares,
            userStakedUSD: baultData.estimatedLpTokensUSD,
            stakes: baultData.positions || [],
            isLoading,
          }
        } else {
          return {
            type: 'rewardVault' as const,
            subType: 'none' as const,
            userStaked: undefined,
            userStakedUSD: undefined,
            stakes: [],
            isLoading,
          }
        }
      }

      default:
        return {
          type: 'none' as const,
          userStaked: undefined,
          userStakedUSD: undefined,
          stakes: [],
          isLoading,
        }
    }
  }, [
    stakingData.stakingType,
    userStakesData,
    rewardVaultData,
    baultData,
    isLoadingUserStakes,
    isLoadingVault,
    isLoadingBault,
  ])

  // Derived state
  const hasStakedBalance = useMemo(() => {
    return position.userStaked && position.userStaked.greaterThan('0')
  }, [position.userStaked])

  return {
    position,
    hasStakedBalance,
    isLoading: position.isLoading,
    kodiakFarm,
  }
}
